import { supabase } from '../supabase';
import type { UserStats, UserActivity, Profile, PaginatedResponse } from '@/types/database.types';

export async function getUserStats(): Promise<UserStats> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Call the RPC function
    const { data, error } = await supabase.rpc('getUserStats');

    if (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }

    if (!data) {
      throw new Error('No data returned from getUserStats');
    }

    // Convert the returned data to the expected UserStats format
    return {
      snapsCreated: data.snapsCreated || 0,
      votesCast: data.votesCast || 0,
      commentsMade: data.commentsMade || 0,
      rank: data.rank || 'Novice',
      activityScore: data.activityScore || 0
    };
  } catch (error) {
    console.error('Error fetching user stats:', error);
    
    // Fallback to default values if the function fails
    return {
      snapsCreated: 0,
      votesCast: 0,
      commentsMade: 0,
      rank: 'Novice',
      activityScore: 0
    };
  }
}

export async function getUserActivity(page = 1, limit = 10): Promise<PaginatedResponse<UserActivity>> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get total count of all activities
  const [snapsResult, votesResult, commentsResult] = await Promise.all([
    supabase.from('snaps').select('*', { count: 'exact', head: true }).eq('creator_id', user.id),
    supabase.from('votes').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
    supabase.from('comments').select('*', { count: 'exact', head: true }).eq('user_id', user.id)
  ]);

  const snapsCount = snapsResult.count || 0;
  const votesCount = votesResult.count || 0;
  const commentsCount = commentsResult.count || 0;
  const totalCount = snapsCount + votesCount + commentsCount;

  // Calculate offset
  const offset = (page - 1) * limit;

  // Initialize activities array
  const activities: UserActivity[] = [];

  // Fetch user's snaps
  if (offset < snapsCount) {
    const { data: snaps } = await supabase
      .from('snaps')
      .select('*')
      .eq('creator_id', user.id)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (snaps) {
      snaps.forEach(snap => {
        activities.push({
          type: 'snap_created',
          id: snap.id,
          title: snap.title,
          created_at: snap.created_at,
          snap_id: snap.id
        });
      });
    }
  }

  // Fetch user's votes with snap information
  const { data: votes } = await supabase
    .from('votes')
    .select(`
      *,
      snaps (
        id,
        title
      ),
      snap_options (
        title
      )
    `)
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (votes) {
    votes.forEach(vote => {
      if (vote.snaps) {
        activities.push({
          type: 'vote_cast',
          id: vote.id,
          title: `Voted for "${vote.snap_options.title}" in "${vote.snaps.title}"`,
          created_at: vote.created_at,
          snap_id: vote.snap_id
        });
      }
    });
  }

  // Fetch user's comments with snap information
  const { data: comments } = await supabase
    .from('comments')
    .select(`
      *,
      snaps (
        id,
        title
      )
    `)
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (comments) {
    comments.forEach(comment => {
      if (comment.snaps) {
        activities.push({
          type: 'comment_added',
          id: comment.id,
          title: `Commented on "${comment.snaps.title}"`,
          created_at: comment.created_at,
          snap_id: comment.snap_id,
          content: comment.content
        });
      }
    });
  }

  // Sort all activities by date
  const sortedActivities = activities.sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return {
    data: sortedActivities,
    total: totalCount,
    page,
    limit
  };
}

export async function getFollowers(userId: string, page = 1, limit = 10): Promise<PaginatedResponse<Profile>> {
  if (!userId) throw new Error('User ID is required');

  // Get total count first
  const { count } = await supabase
    .from('follows')
    .select('*', { count: 'exact', head: true })
    .eq('following_id', userId);

  // Get followers with their profiles
  const { data: followers } = await supabase
    .from('follows')
    .select(`
      follower:profiles!follows_follower_id_fkey(
        id:user_id,
        username,
        avatar_url,
        created_at
      )
    `)
    .eq('following_id', userId)
    .range((page - 1) * limit, page * limit - 1);

  // Get current user's follow status for each follower
  const { data: { user } } = await supabase.auth.getUser();
  const profiles = await Promise.all(followers?.map(async f => {
    const { data: isFollowing } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', user?.id)
      .eq('following_id', f.follower.id)
      .maybeSingle();

    return {
      ...f.follower,
      is_following: !!isFollowing
    };
  }) || []);

  return {
    data: profiles,
    total: count || 0,
    page,
    limit
  };
}

export async function getFollowing(userId: string, page = 1, limit = 10): Promise<PaginatedResponse<Profile>> {
  if (!userId) throw new Error('User ID is required');

  // Get total count first
  const { count } = await supabase
    .from('follows')
    .select('*', { count: 'exact', head: true })
    .eq('follower_id', userId);

  // Get following users with their profiles
  const { data: following } = await supabase
    .from('follows')
    .select(`
      following:profiles!follows_following_id_fkey(
        id:user_id,
        username,
        avatar_url,
        created_at
      )
    `)
    .eq('follower_id', userId)
    .range((page - 1) * limit, page * limit - 1);

  // Get current user's follow status for each following user
  const { data: { user } } = await supabase.auth.getUser();
  const profiles = await Promise.all(following?.map(async f => {
    const { data: isFollowing } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', user?.id)
      .eq('following_id', f.following.id)
      .maybeSingle();

    return {
      ...f.following,
      is_following: !!isFollowing
    };
  }) || []);

  return {
    data: profiles,
    total: count || 0,
    page,
    limit
  };
}

export async function updateProfile(username: string, avatarUrl: string | null): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Check if username is already taken (excluding current user)
  const { data: existingUser } = await supabase
    .from('profiles')
    .select('id')
    .eq('username', username)
    .neq('user_id', user.id)
    .maybeSingle();

  if (existingUser) {
    throw new Error('username_key');
  }

  // Update profile
  const { error } = await supabase
    .from('profiles')
    .update({
      username,
      avatar_url: avatarUrl,
      updated_at: new Date().toISOString()
    })
    .eq('user_id', user.id);

  if (error) throw error;
}

export async function followUser(userId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  if (!userId) throw new Error('Target user ID is required');
  if (user.id === userId) throw new Error('Cannot follow yourself');

  // Use RPC function to handle follow/unfollow in a single operation
  const { error } = await supabase.rpc('toggle_follow', {
    target_id: userId
  });

  if (error) throw error;
}

export async function unfollowUser(userId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  if (!userId) throw new Error('Target user ID is required');

  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', user.id)
    .eq('following_id', userId);

  if (error) throw error;
}